import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../lib/api";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { es, enUS } from "date-fns/locale";
import {
  Crown, XCircle, ArrowUpRight, CreditCard,
  CalendarBlank, ShieldCheck, User, SignOut,
} from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import AdminSection from "../components/admin/AdminSection";

const STATUS_CONFIG = {
  active:   { labelKey: "profile.active",   color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/25" },
  trialing: { labelKey: "profile.trialing", color: "text-blue-400",    bg: "bg-blue-500/10 border-blue-500/25" },
  inactive: { labelKey: "profile.inactive", color: "text-zinc-400",    bg: "bg-white/[0.04] border-white/[0.06]" },
  canceled: { labelKey: "profile.canceled", color: "text-red-400",     bg: "bg-red-500/10 border-red-500/25" },
  past_due: { labelKey: "profile.past_due", color: "text-yellow-400",  bg: "bg-yellow-500/10 border-yellow-500/25" },
};

const PLAN_NAME_KEYS = { free: "profile.plan_free", premium: "profile.plan_premium", vip: "profile.plan_vip" };

const TX_PLAN_KEYS = {
  weekly: "profile.tx_weekly", monthly: "profile.tx_monthly",
  premium_weekly: "profile.tx_weekly", premium_monthly: "profile.tx_monthly",
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.inactive;
  return (
    <span className={`chip text-[11px] ${cfg.bg} ${cfg.color}`} data-testid="profile-status-badge">
      {t(cfg.labelKey)}
    </span>
  );
}

function SkeletonLine({ w = "w-full", h = "h-5" }) {
  return <div className={`${h} ${w} bg-white/[0.04] rounded-md animate-pulse`} />;
}

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const nav = useNavigate();

  const fmt = (iso) => {
    if (!iso) return "–";
    try {
      const isEn = i18n.language.startsWith("en");
      const locale = isEn ? enUS : es;
      const pattern = isEn ? "MMMM d, yyyy" : "d 'de' MMMM, yyyy";
      return format(parseISO(iso), pattern, { locale });
    } catch {
      return String(iso).slice(0, 10);
    }
  };
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/me/avatar", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success(t("profile.change_photo"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    }
    setAvatarUploading(false);
    e.target.value = "";
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get("/profile/subscription");
        setSub(res.data);
      } catch (err) {
        console.error("[Profile] subscription fetch failed:", err?.response?.data || err.message);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = async () => {
    setCanceling(true);
    try {
      // Prefer Stripe subscription cancel (cancel at period end) when applicable
      if (sub?.stripe_subscription_id || user?.stripe_subscription_id) {
        const r = await apiClient.post("/subscription/cancel");
        toast.success(r.data?.message || "Suscripción cancelada — mantienes acceso hasta el fin del período");
        setSub(prev => prev ? { ...prev, subscription_cancel_at_period_end: true } : prev);
      } else {
        await apiClient.post("/profile/cancel-subscription");
        setSub(prev => prev ? { ...prev, subscription_status: "canceled", plan: "free" } : prev);
        toast.success(t("profile.canceled"));
      }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setCanceling(false);
    }
  };

  const status = sub?.subscription_status || user?.subscription_status || "inactive";
  const plan = sub?.plan || user?.plan || "free";
  const expiresAt = sub?.subscription_expires_at || user?.subscription_expires_at;
  const paymentHistory = sub?.payment_history || [];
  const canCancel = ["active", "trialing"].includes(status);
  const isFreeTier = plan === "free" || !["active", "trialing"].includes(status);

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 pt-8 pb-16">

      {/* Page header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-2">{t("profile.my_account")}</div>
          <h1 className="text-display font-black text-3xl lg:text-4xl tracking-tighter leading-none">
            {t("profile.title")}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {t("profile.sub_management")}
          </p>
        </div>
        <button
          data-testid="profile-logout-btn"
          onClick={() => { logout(); nav("/login"); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-500/20 bg-red-500/5 text-red-400 hover:bg-red-500/10 hover:border-red-500/35 transition-all text-sm font-semibold flex-shrink-0 mt-1"
        >
          <SignOut weight="bold" className="w-4 h-4" />
          {t("profile.logout")}
        </button>
      </div>

      {/* User info card */}
      <AdminSection icon={User} title="Mi cuenta" subtitle={t("profile.sub_management")} color="amber" testid="profile-user-card" className="mb-6">
        <div className="flex items-center gap-5">
        {/* Avatar estilo Instagram: círculo limpio con aro dorado, toca para cambiar */}
        <div className="flex-shrink-0 flex flex-col items-center gap-2">
          <button
            data-testid="profile-avatar-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={avatarUploading}
            title="Cambiar foto"
            className="rounded-full p-[3px] transition-transform active:scale-95"
            style={{ background: "linear-gradient(135deg, #F59E0B, #FCD34D, #B45309)" }}
          >
            <span className="block w-20 h-20 rounded-full overflow-hidden bg-[#14141a] border-2 border-[#0b0b10]">
              {avatarUploading ? (
                <span className="w-full h-full flex items-center justify-center">
                  <span className="w-6 h-6 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
                </span>
              ) : user?.avatar_url ? (
                <img src={user.avatar_url} alt="foto de perfil" className="w-full h-full object-cover" />
              ) : (
                <span className="w-full h-full flex items-center justify-center text-yellow-400 font-black text-3xl" style={{ fontFamily: "'Sora', sans-serif" }}>
                  {(user?.name || "?").charAt(0).toUpperCase()}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-[11px] font-bold text-yellow-400/80 hover:text-yellow-300 transition-colors"
          >
            Cambiar foto
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleAvatarChange}
            data-testid="profile-avatar-file-input"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-display font-bold text-xl truncate" data-testid="profile-user-name">
            {user?.name}
          </div>
          <div className="text-muted-foreground text-sm mt-0.5" data-testid="profile-user-email">
            {user?.email}
          </div>
          <div className="flex items-center gap-1.5 mt-2">
            <CalendarBlank className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-[11px] text-zinc-500">
              {t("profile.member_since")} {fmt(user?.created_at || sub?.created_at)}
            </span>
          </div>
        </div>
      </div>
      </AdminSection>

      {/* Subscription card */}
      <AdminSection
        icon={user?.role === "admin" ? ShieldCheck : Crown}
        title={user?.role === "admin" ? "Acceso" : t("profile.current_sub")}
        subtitle="Tu plan y estado actual"
        color="emerald"
        testid="profile-subscription-card"
        className="mb-6"
      >

        {/* Admin users: show admin badge instead of subscription info */}
        {user?.role === "admin" ? (
          <div className="flex items-center gap-4" data-testid="profile-admin-badge">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.2), rgba(212,175,55,0.05))", border: "1px solid rgba(212,175,55,0.4)" }}>
              <ShieldCheck weight="fill" className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <div className="text-display font-black text-2xl uppercase tracking-tight text-white">
                Administrador
              </div>
              <div className="text-xs text-zinc-500 mt-1">Acceso completo al sistema · Sin restricciones de plan</div>
            </div>
          </div>
        ) : loading ? (
          <div className="space-y-3">
            <SkeletonLine w="w-1/3" h="h-8" />
            <SkeletonLine w="w-1/2" h="h-4" />
            <SkeletonLine w="w-1/4" h="h-10" />
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-start gap-5 mb-6">
              <div className="flex items-center gap-3">
                {plan === "vip" ? (
                  <Crown weight="fill" className="w-10 h-10 text-yellow-400 flex-shrink-0" />
                ) : plan === "premium" ? (
                  <ShieldCheck weight="fill" className="w-10 h-10 text-yellow-400 flex-shrink-0" />
                ) : (
                  <User weight="regular" className="w-10 h-10 text-zinc-500 flex-shrink-0" />
                )}
                <div>
                  <div className="text-display font-black text-3xl uppercase tracking-tight" data-testid="profile-plan-name">
                    {t(PLAN_NAME_KEYS[plan] || "profile.plan_free")}
                  </div>
                  <div className="mt-1">
                    <StatusBadge status={status} />
                  </div>
                </div>
              </div>

              {expiresAt && (
                <div className="sm:ml-auto bg-white/[0.03] border border-white/[0.06] rounded-lg px-4 py-3 text-right">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] mb-0.5">
                    {status === "trialing" ? t("profile.trial_ends") : t("profile.expires")}
                  </div>
                  <div className="text-sm font-semibold" data-testid="profile-expiry-date">
                    {fmt(expiresAt)}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              {isFreeTier ? (
                <Link
                  to="/plans"
                  className="btn-primary text-sm"
                  data-testid="profile-upgrade-btn"
                >
                  <Crown weight="fill" className="w-4 h-4" />
                  {t("profile.upgrade_plan")}
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              ) : (
                <Link
                  to="/plans"
                  className="btn-secondary text-sm"
                  data-testid="profile-see-plans-btn"
                >
                  {t("profile.see_plans")}
                </Link>
              )}

              {canCancel && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      className="btn-ghost text-sm text-red-400 hover:text-red-300"
                      data-testid="profile-cancel-btn"
                      disabled={canceling}
                    >
                      <XCircle className="w-4 h-4" />
                      {canceling ? t("profile.canceling") : t("profile.cancel_sub")}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("profile.cancel_confirm")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("profile.cancel_description", { date: fmt(expiresAt) })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="profile-cancel-abort-btn">
                        {t("profile.keep_sub")}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleCancel}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="profile-cancel-confirm-btn"
                      >
                        {t("profile.yes_cancel")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </>
        )}
      </AdminSection>

      {/* Payment history */}
      <AdminSection
        icon={CreditCard}
        title={t("profile.payment_history")}
        subtitle="Tus transacciones"
        color="sky"
        testid="profile-payment-history"
      >

        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => <SkeletonLine key={i} h="h-14" />)}
          </div>
        ) : paymentHistory.length === 0 ? (
          <div className="py-10 flex flex-col items-center gap-3 text-center">
            <div className="w-12 h-12 rounded-full bg-white/[0.04] flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-zinc-600" />
            </div>
            <p className="text-sm text-zinc-500">{t("profile.no_payments_alt")}</p>
            {isFreeTier && (
              <Link to="/plans" className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors">
                {t("profile.view_plans")}
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {paymentHistory.map((tx) => (
              <div
                key={tx.id || tx.session_id}
                className="flex items-center justify-between py-3 px-4 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] transition-colors"
                data-testid={`payment-row-${tx.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4 text-zinc-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">
                      {t(TX_PLAN_KEYS[tx.plan_id]) || tx.plan_id || t("profile.plan_label")}
                    </div>
                    <div className="text-[11px] text-zinc-500">{fmt(tx.created_at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-semibold number-ticker">
                    ${tx.amount?.toFixed(2)} {(tx.currency || "usd").toUpperCase()}
                  </div>
                  <span className={`chip text-[10px] ${
                    tx.payment_status === "paid"
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/25"
                      : "bg-zinc-500/10 text-zinc-400 border-zinc-500/25"
                  }`}>
                    {tx.payment_status === "paid" ? t("profile.paid") : t("profile.pending")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminSection>
    </div>
  );
}
